import type { Dispatch, PickActionFromState } from '~/utils/redux';
import type { NotYetTyped } from '~/utils/types';
import type { ImmutableCustomSection, Section, WebBuilderState } from './WebBuilderReducer';

export const TOGGLE_IFRAME_RENDER = 'TOGGLE_IFRAME_RENDER';
export const SET_LOADING_PREVIEW = 'SET_LOADING_PREVIEW';
export const SET_SELECTED_NAV_LINK = 'SET_SELECTED_NAV_LINK';
export const SET_SELECTED_PAGE = 'SET_SELECTED_PAGE';
export const SET_PREVIEW_SELECTED_SECTION = 'SET_PREVIEW_SELECTED_SECTION';
export const SET_ACTIVE_SECTION = 'SET_ACTIVE_SECTION';
export const SET_ADD_SECTION_OPENED = 'SET_ADD_SECTION_OPENED';
export const SET_FORM_ERROR = 'SET_FORM_ERROR';

export type WebBuilderAction =
  ToggleIframeRenderAction |
  SetLoadingPreviewAction |
  SetSelectedNavLinkAction |
  SetSelectedPageAction |
  SetPreviewSelectedSectionAction |
  SetActiveSectionAction |
  SetAddSectionOpenedAction |
  SetFormErrorAction;

export type ToggleIframeRenderAction = PickActionFromState<WebBuilderState, typeof TOGGLE_IFRAME_RENDER>;
// This function forces the iframe inside the webbuilder to re-render
export function toggleIframeRender() {
  return (dispatch: Dispatch<ToggleIframeRenderAction>) => {
    dispatch({
      type: TOGGLE_IFRAME_RENDER,
    });
  };
}

export type SetLoadingPreviewAction = PickActionFromState<WebBuilderState, typeof SET_LOADING_PREVIEW, 'loadingPreview'>;
export function setLoadingPreview(loadingPreview: boolean) {
  return (dispatch: Dispatch<SetLoadingPreviewAction>) => {
    dispatch({
      loadingPreview,
      type: SET_LOADING_PREVIEW,
    });
  };
}

export type SetSelectedNavLinkAction = PickActionFromState<WebBuilderState, typeof SET_SELECTED_NAV_LINK, 'selectedNavLink'>;
export function setSelectedNavLink(selectedNavLink: NotYetTyped | null) {
  return (dispatch: Dispatch<SetSelectedNavLinkAction>) => {
    dispatch({
      selectedNavLink,
      type: SET_SELECTED_NAV_LINK,
    });
  };
}

export type SetSelectedPageAction = PickActionFromState<WebBuilderState, typeof SET_SELECTED_PAGE, 'selectedPageId' | 'pageUrl'>;
export function setSelectedPage(selectedPageId: number | null, pageUrl: string | null = null) {
  return (dispatch: Dispatch<SetSelectedPageAction>) => {
    dispatch({
      pageUrl,
      selectedPageId,
      type: SET_SELECTED_PAGE,
    });
  };
}

// SET_PREVIEW_SELECTED_SECTION spreads data into state, so it can contain any WebBuilderState fields
export type SetPreviewSelectedSectionAction = {
  type: typeof SET_PREVIEW_SELECTED_SECTION;
  data: Partial<WebBuilderState>;
};
export function setPreviewSelectedSection(data: Partial<WebBuilderState>) {
  return (dispatch: Dispatch<SetPreviewSelectedSectionAction>) => {
    dispatch({
      data,
      type: SET_PREVIEW_SELECTED_SECTION,
    });
  };
}

// Action field is "section" but state field is "activeSection", so we can't use PickActionFromState
export type SetActiveSectionAction = {
  type: typeof SET_ACTIVE_SECTION;
  section: Section | ImmutableCustomSection | null;
};
export function setActiveSection(section: Section | ImmutableCustomSection | null) {
  return (dispatch: Dispatch<SetActiveSectionAction>) => {
    dispatch({
      section,
      type: SET_ACTIVE_SECTION,
    });
  };
}

// Action field is "payload" but state field is "addSectionOpened", so we can't use PickActionFromState
export type SetAddSectionOpenedAction = {
  type: typeof SET_ADD_SECTION_OPENED;
  payload: boolean;
};
export function setAddSectionOpened(payload: boolean) {
  return (dispatch: Dispatch<SetAddSectionOpenedAction>) => {
    dispatch({
      payload,
      type: SET_ADD_SECTION_OPENED,
    });
  };
}

export type SetFormErrorAction = PickActionFromState<WebBuilderState, typeof SET_FORM_ERROR, 'formError'>;
export function setFormError(formError: Error | null) {
  return (dispatch: Dispatch<SetFormErrorAction>) => {
    dispatch({
      formError,
      type: SET_FORM_ERROR,
    });
  };
}
